import type { Experiment, Result } from '@growthbook/growthbook-react';
import snakecaseKeys from 'snakecase-keys';

import config from 'config';
import { rumService } from 'services/rum';

const isProd = config.PARTNERS_ENVIRONMENT === 'production';

export type RecordExperimentAssignmentPayload = {
  result: {
    experiment: Experiment<unknown>;
    experimentResult: Result<unknown>;
  };
  clientId: string;
  participantType: string;
  evaluatedAt?: string;
  context?: Record<string, unknown>;
};

/**
 * Records an experiment assignment (bucketing decision) to the backend API.
 *
 * This is a fire-and-forget operation that logs errors but does not throw,
 * ensuring that experiment tracking failures don't impact user experience.
 *
 * Used when GrowthBook's trackingCallback is triggered, indicating a user
 * has been assigned to a specific experiment variant.
 *
 * @param payload - The experiment assignment data
 * @returns Response if successful, undefined if failed
 *
 * @example
 * ```typescript
 * recordExperimentAssignment({
 *   result: {
 *     experiment: experiment,
 *     experimentResult: result,
 *   },
 *   clientId: 'partners-self-signup-spa',
 *   participantType: 'user_id',
 *   evaluatedAt: new Date().toISOString(),
 * });
 * ```
 */
export async function recordExperimentAssignment(
  payload: RecordExperimentAssignmentPayload,
): Promise<void> {
  const url = `${config.USER_EXPERIMENT_API_URL}/growthbook/results`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-api-key': config.USER_EXPERIMENT_API_KEY,
      },
      body: JSON.stringify(snakecaseKeys(payload, { deep: true })),
    });

    // 403s here are the user-experiment-api WAF geo-blocking bot/proxy traffic
    // (confirmed via WAF logs), not an app-side failure — don't report as a RUM error.
    if (!response.ok && response.status !== 403) {
      rumService.addError(
        new Error(
          `[GrowthBook Decision][recordExperimentAssignment] ${response.status} (${response.statusText})`,
        ),
      );
    }

    // Guarded on response.ok: a WAF block page (or any other failure body)
    // isn't valid JSON, and parsing it here would throw into the catch below,
    // re-reporting the very statuses (like 403) we just chose not to report.
    if (!isProd && response.ok) {
      const result = await response.json();
      // eslint-disable-next-line no-console
      console.log('[GrowthBook decision event]', { result });
    }
  } catch (error) {
    rumService.addError(
      new Error(
        `[GrowthBook Decision][recordExperimentAssignment] ${
          (error as Error)?.message
        }`,
      ),
    );
  }
}
